<script setup lang="ts">
import {
  GraphDataProviderColumn,
  GraphDataProviderQueryRoot,
  GraphDataProviderState,
  useContext,
  useGraphDataProvider,
  usePhrases,
} from "@hantera/portal-app";
import { DataTableContextMenuProvider, ListDetailLayout, TextInput } from "@hantera/design-system";
import { computed, ref, toRaw, watch } from "vue";
import useBackend from "@/composition/useBackend";
import useGraphPhrases from "@/composition/use-graph-phrases";
import useGraphAvailableFields from "@/composition/use-graph-available-fields";
import InventorySkuDetails from "./InventorySkuDetails.vue";

const context = useContext();
const { user } = useBackend();
const t = usePhrases("sales.inventory");
const graphPhrases = useGraphPhrases();
const { getAvailableFields } = useGraphAvailableFields();

context.currentView!.value!.name.value = "Inventory";

const searchPhrase = ref(context.currentView.value?.query.phrase ?? "");

const query = computed<GraphDataProviderQueryRoot>(() => ({
  edge: "skus",
  dataFields: ["skuId"],
  orderBy: "createdAt desc",
  phrase: searchPhrase.value,
  availableFields: getAvailableFields("sku").map((c) => ({
    field: c,
    label: graphPhrases.field("sku", c),
  })),
}));

const defaultColumns = [
  { field: "skuNumber", queryPath: ["skus"] },
  { field: "createdAt", queryPath: ["skus"] },
];

const columns = ref<GraphDataProviderColumn[]>(
  user.value.settings["sales.skus.columns"] ?? defaultColumns
);

watch(columns, () => {
  user.updateSettings({ "sales.skus.columns": toRaw(columns.value) });
}, { deep: true });

const state = ref<GraphDataProviderState>(context.currentView.value!.state.listState ?? {
  filters: {},
});

watch(state, () => {
  context.currentView.value!.state.listState = state.value;
}, { deep: true });

const live = ref<boolean>(context.currentView.value!.state.live ?? false) // Live inventory is genereally not needed, hence live=false by default

watch(live, () => {
  context.currentView.value!.state.live = live.value
})

const selection = ref<unknown>(context.currentView.value!.state.selection);

watch(selection, () => {
  context.currentView.value!.state.selection = selection.value;
});


const provider = useGraphDataProvider(query, columns, state, {
  live
});

const dataTableContextMenuProvider = computed<DataTableContextMenuProvider>(() => ({
  table: [[{
    label: 'Reset Layout',
    action: () => {
      columns.value = defaultColumns
    }
  }, {
    label: live.value ? 'Disable live updates' : 'Enable live updates',
    action() {
      live.value = !live.value
    }
  }, {
    label: 'Refresh',
    disabled: live.value,
    action() {
      provider.refresh()
    }
  }]]
}))

</script>

<template>
  <ListDetailLayout
    :provider="provider"
    :detail-component="InventorySkuDetails"
    v-model:selection="selection"
    cache-key="sales_skus_layout"
    :data-table-context-menu-provider="dataTableContextMenuProvider"
  >
    <template #list-toolbar>
      <div class="list-toolbar">
        <div class="search">
          <TextInput type="search" v-model="searchPhrase" :placeholder="t('search')" />
        </div>
      </div>
    </template>
  </ListDetailLayout>
</template>

<style scoped>
.list-toolbar {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 5px;
}
</style>
